// Natural-flow feeds and threads render media at a small set of fixed heights bucketed by
// aspect ratio. Because the height is a constant per class (independent of container width),
// offscreen segment estimates stay aligned with mounted media and avoid collapse-then-grow
// scroll jumps.

import { replyMediaEnabled } from 'lib/replyMedia'

// Retained while legacy static-geometry tests are retired. Production routing no longer depends
// on a per-user feature: natural flow is the shared feed/thread default.
export const SCROLL_STATIC_GEOMETRY_FEATURE = 'scroll_static_geometry'
const NATURAL_FLOW_DEFAULT_ENABLED = true

export function mediaBucketsEnabled(): boolean {
    return NATURAL_FLOW_DEFAULT_ENABLED
}

export function naturalFlowEnabled(): boolean {
    return NATURAL_FLOW_DEFAULT_ENABLED
}

export function naturalFeedEnabled(): boolean {
    return naturalFlowEnabled()
}

type NaturalFeedFilter = {
    questId?: unknown
    streams?: unknown
    memes?: unknown
    search?: unknown
    ordinals?: unknown
    user_ordinals?: unknown
}

// Panel-level effects run outside QuestStream, so they need the same routing decision as
// Panel/QuestStream before opting out of legacy scroll compensation.
export function naturalFeedLayoutEnabled(filter?: NaturalFeedFilter | null): boolean {
    if (!naturalFeedEnabled() || !filter) return false
    const isOrdinalsMediaStream = !filter.search && (filter.ordinals || filter.user_ordinals)
    return !(filter.questId || filter.streams || filter.memes || isOrdinalsMediaStream)
}

export function naturalThreadLayoutEnabled(filter?: NaturalFeedFilter | null): boolean {
    return naturalFlowEnabled() && !!filter?.questId
}

// Fixed display height per aspect class: landscape short, square taller, portrait tallest
// (capped so a long screenshot doesn't dominate the feed). Tunable.
//
// availableTileWidth (optional): the pixel width the media frame actually renders at. When the
// contain-fit media cannot fill the bucket height at that width (narrow mobile cards), the
// frame is clamped to the media's own height so the row reserves no letterbox band above and
// below. Both the renderers and the height estimators must derive this width from the same
// source (see mobilePayloadMediaContentWidth) so reserved height stays equal to rendered
// height.
// The portrait bucket doubles as the tallest height budget any single media unit may claim —
// multi-tile payload grids cap their TOTAL at this same budget (payloadMediaGridLayout).
export const MAX_BUCKETED_MEDIA_HEIGHT = 500

export function bucketedMediaHeight(
    width?: number | null,
    height?: number | null,
    availableTileWidth?: number | null,
): number {
    const w = Number(width)
    const h = Number(height)
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 360

    const aspect = w / h
    const bucket = aspect >= 1.15 ? 300 : aspect > 0.85 ? 400 : MAX_BUCKETED_MEDIA_HEIGHT
    const tileWidth = Number(availableTileWidth)
    if (Number.isFinite(tileWidth) && tileWidth > 0) {
        return Math.min(bucket, Math.max(1, Math.round(tileWidth / aspect)))
    }
    return bucket
}

// COVER-FIT grids (tweet/X media): a multi-tile grid is ONE media unit whose frame is the
// tallest tile's bucket — the same cap a single image of that class gets — and two-row layouts
// subdivide that frame. Summing per-tile buckets instead lets 3-4 portrait screenshots stack to
// 1000+px, exactly what the per-class caps exist to prevent. These grids crop into their cells,
// so the subdivided rows lose sharpness, not content reachable nowhere else — the lightbox
// shows the full image. Renderers and estimators must both take grid heights from here so
// reserved stays equal to rendered. rowHeight is null for the single-row 2-up layout.
export function bucketedMediaGridLayout(
    tileHeights: number[],
    gapPx: number,
): { height: number; rowHeight: number | null } | null {
    if (tileHeights.length <= 1) return null

    const frameHeight = Math.max(...tileHeights)
    if (tileHeights.length === 2) return { height: frameHeight, rowHeight: null }

    const rowHeight = Math.max(1, Math.floor((frameHeight - gapPx) / 2))
    return { height: rowHeight * 2 + gapPx, rowHeight }
}

// CONTAIN-FIT 2-up grids (answer payload media, which is never cropped): reply cards explicitly
// request square tiles sized to their computed column width. Other surfaces keep natural
// max-of-pair heights — width-clamped tiles already hug their media, and shrinking those rows
// would only re-letterbox them — but cap the grid TOTAL at the tallest single-media budget (the
// portrait bucket). When that cap engages (only possible with 3-4 tiles), the grid subdivides
// the budget into two uniform rows. Renderers (MediaContainer) and estimators
// (estimateAnswerPayloadHeight) must both derive grid heights from here so reserved stays equal
// to rendered.
export function payloadMediaGridLayout(
    tileHeights: number[],
    gapPx: number,
    { squareTileHeight = null }: { squareTileHeight?: number | null } = {},
): { height: number; uniformTileHeight: number | null } | null {
    const count = tileHeights.length
    if (count === 0) return null

    const requestedSquareTileHeight = Number(squareTileHeight)
    if (count > 1 && Number.isFinite(requestedSquareTileHeight) && requestedSquareTileHeight > 0) {
        const uniformTileHeight = Math.max(1, Math.round(requestedSquareTileHeight))
        const rowCount = Math.ceil(count / 2)
        return {
            height: rowCount * uniformTileHeight + Math.max(0, rowCount - 1) * gapPx,
            uniformTileHeight,
        }
    }

    let pairRowsHeight = 0
    for (let i = 0; i < count; i += 2) {
        pairRowsHeight += Math.max(...tileHeights.slice(i, i + 2))
        if (i > 0) pairRowsHeight += gapPx
    }
    if (pairRowsHeight <= MAX_BUCKETED_MEDIA_HEIGHT) {
        return { height: pairRowsHeight, uniformTileHeight: null }
    }

    const uniformTileHeight = Math.max(1, Math.floor((MAX_BUCKETED_MEDIA_HEIGHT - gapPx) / 2))
    return { height: uniformTileHeight * 2 + gapPx, uniformTileHeight }
}

// ——— Mobile media width contract ———
// Mobile is a single full-viewport panel and the natural feed/thread rows render full-bleed
// (.full-bleed-quests escapes the panel padding), so the payload media box is viewport-derived:
// the only horizontal chrome is the .mobilePayloadContainer 15+15 padding (answer.module.scss —
// aligns media with the 15px mobile answer inset). Renderers and estimators share these helpers
// so the clamped frame heights match on both sides.
export const MOBILE_PAYLOAD_CONTAINER_HORIZONTAL_PADDING_PX = 30
const MOBILE_PAYLOAD_MEDIA_HORIZONTAL_CHROME_PX = MOBILE_PAYLOAD_CONTAINER_HORIZONTAL_PADDING_PX
export const MOBILE_REPLY_MEDIA_LEFT_INDENT_PX = 65
export const MOBILE_REPLY_MEDIA_RIGHT_INSET_PX = 15
const MOBILE_REPLY_MEDIA_HORIZONTAL_CHROME_PX =
    MOBILE_REPLY_MEDIA_LEFT_INDENT_PX + MOBILE_REPLY_MEDIA_RIGHT_INSET_PX
const MOBILE_REPLY_MEDIA_EXTRA_LEFT_INDENT_PX =
    MOBILE_REPLY_MEDIA_LEFT_INDENT_PX - MOBILE_PAYLOAD_CONTAINER_HORIZONTAL_PADDING_PX / 2

export const PAYLOAD_MEDIA_GRID_GAP_PX = 6
// .payloadBody 1px borders around grid tiles (zeroed for the single completed media case).
const PAYLOAD_MEDIA_TILE_BORDER_PX = 2

function isMobileViewportClient(): boolean {
    if (typeof window === 'undefined') return false
    if (Boolean((window as any).KNOVIGATOR_IS_MOBILE)) return true

    // Keep this low-level geometry module safe to import in SSR and Node test bundles. Importing
    // react-device-detect here eagerly reads the global navigator, which is not available in
    // those environments. This mirrors the package's mobile/tablet decision closely enough for
    // the payload class that Answer renders from its isMobile value, including iPadOS desktop UA.
    const clientNavigator = window.navigator
    const userAgent = String(clientNavigator?.userAgent || '')
    const isIPadOs =
        clientNavigator?.platform === 'MacIntel' && Number(clientNavigator?.maxTouchPoints) > 1
    return isIPadOs || /Android|iPad|iPhone|iPod|Mobile|Tablet/i.test(userAgent)
}

// Width of the payload media content box on mobile, or null when the mobile clamp does not
// apply (desktop panels vary per layout, so their frames keep the plain aspect buckets).
export function mobilePayloadMediaContentWidth({ postLens = false }: { postLens?: boolean } = {}):
    | number
    | null {
    if (!isMobileViewportClient()) return null

    const viewport = Number(window.innerWidth)
    if (!Number.isFinite(viewport) || viewport <= 0) return null

    const horizontalChrome =
        postLens && replyMediaEnabled()
            ? MOBILE_REPLY_MEDIA_HORIZONTAL_CHROME_PX
            : MOBILE_PAYLOAD_MEDIA_HORIZONTAL_CHROME_PX
    return Math.max(120, Math.round(viewport) - horizontalChrome)
}

// Link/tweet/embed estimators already subtract the standard 15px left inset internally. The
// reply-media layout moves those surfaces to the 65px reply column, so narrow their input width
// only by the additional 50px and keep wrapping reservations matched to rendered cards.
export function mobileReplyMediaEstimatedWidth({
    postLens = false,
    cardWidth = null,
}: { postLens?: boolean; cardWidth?: number | null } = {}): number | null {
    const width = Number(cardWidth)
    if (!Number.isFinite(width) || width <= 0) return null
    if (!postLens || !replyMediaEnabled() || !isMobileViewportClient()) return width

    return Math.max(120, Math.round(width) - MOBILE_REPLY_MEDIA_EXTRA_LEFT_INDENT_PX)
}

// ——— Post-lens (reply card) media width contract ———
// stream_reply_cards and thread_reply_cards use reply anatomy, with media indented into the
// byline column (answer.module.scss .replyCardAnswerDesktop): side margin 20 + 40px avatar +
// 10px gap on the left (70 total, desktop only) and the standard 15px payload margin on the
// right. Frames clamp to this narrower column exactly like the mobile contract — renderers and
// estimators must derive the width from these same helpers so reserved height stays equal to
// rendered height. On mobile, the reply-media layout switches the contract above from the
// stock 15px insets to the reply column's 65px left inset and 15px right inset.
export const POST_LENS_MEDIA_LEFT_INDENT_PX = 70
export const POST_LENS_MEDIA_RIGHT_MARGIN_PX = 15
const POST_LENS_MEDIA_HORIZONTAL_CHROME_PX =
    POST_LENS_MEDIA_LEFT_INDENT_PX + POST_LENS_MEDIA_RIGHT_MARGIN_PX

// Width of the payload media content box inside a post-lens card, derived from the card/row
// width both sides already share (AnswerInView's estimatedWidth ≡ the quest stream row width).
// Null when the card width is unknown — callers then keep the plain aspect buckets, and the
// remembered measured height converges the residual exactly as before this contract existed.
export function postLensPayloadMediaContentWidth(cardWidth?: number | null): number | null {
    const width = Number(cardWidth)
    if (!Number.isFinite(width) || width <= 0) return null

    return Math.max(120, Math.round(width) - POST_LENS_MEDIA_HORIZONTAL_CHROME_PX)
}

// Nested answers inside PostEmbed retain the standard 15px payload margins on both sides.
// PostEmbed passes the already-narrowed nested Answer width, so this is the remaining chrome
// between that Answer and the payload media grid.
export const EMBED_PAYLOAD_MEDIA_HORIZONTAL_CHROME_PX = 30

export function embedPayloadMediaContentWidth(cardWidth?: number | null): number | null {
    const width = Number(cardWidth)
    if (!Number.isFinite(width) || width <= 0) return null

    return Math.max(120, Math.round(width) - EMBED_PAYLOAD_MEDIA_HORIZONTAL_CHROME_PX)
}

// Single entry point for the payload media content width: the mobile viewport contract wins
// whenever it applies (and narrows flagged post-lens cards), except nested embeds which derive
// from their own card rather than the viewport. Everything else keeps width-independent buckets.
export function payloadMediaContentWidth({
    postLens = false,
    embed = false,
    cardWidth = null,
}: { postLens?: boolean; embed?: boolean; cardWidth?: number | null } = {}): number | null {
    if (embed) return embedPayloadMediaContentWidth(cardWidth)

    const mobileWidth = mobilePayloadMediaContentWidth({ postLens })
    if (mobileWidth != null) return mobileWidth

    return postLens ? postLensPayloadMediaContentWidth(cardWidth) : null
}

// Frame width of one tile in the 2-up payload grid. On mobile the .mediaSection side margins
// are zeroed (mediaSectionMobile), so the grid spans the full payload content width in both
// the single-kind and the mixed-kind row cases.
export function payloadMediaGridTileWidth(contentWidth?: number | null): number | null {
    const width = Number(contentWidth)
    if (!Number.isFinite(width) || width <= 0) return null

    return Math.max(
        40,
        Math.floor((width - PAYLOAD_MEDIA_GRID_GAP_PX) / 2) - PAYLOAD_MEDIA_TILE_BORDER_PX,
    )
}

// Link previews and tweet/X cards reserve a CONSTANT height (not per-item dims): the height
// estimators only see a boolean for url/embeds, so a constant is what keeps estimate ===
// rendered and stops the collapse-then-grow scroll jump. Generic link previews keep this
// constant outer frame while the image slot flexes around title/description metadata.
export const RESERVED_URL_CARD_HEIGHT = 320
export const RESERVED_URL_CARD_IMAGE_HEIGHT = 160
export const RESERVED_YOUTUBE_EMBED_HEIGHT = 384

// Media without known dims still gets a PINNED frame — the square bucket — instead of natural
// aspect rendering. Static geometry must never depend on client runtime discoveries: backend
// dims refine WHICH bucket, while dimensionless media stays in this fallback bucket.
// Worst case for an unusual aspect is letterboxing inside the frame.
export const RESERVED_DIMLESS_MEDIA_HEIGHT = 400
// Audio recordings use the same recording attachment fields as video, but render only the
// compact transport controls. Reserving the dimensionless-video frame leaves a large blank row.
export const RESERVED_AUDIO_RECORDING_HEIGHT = 140
// Payload audio wears the artwork card (AudioAttachmentCard): a landscape stage with the
// transport docked on its scrim, so it reserves like a landscape IMAGE tile — bucketedMediaHeight's
// landscape class — rather than the old transport band. Pinned, not derived from the tile width:
// audio carries no intrinsic dims, and static geometry must not depend on a width the estimator
// cannot see; the artwork covers the frame, so an unusual stage width crops rather than
// letterboxes. The card, PayloadContainer's CSS custom property and the height estimator all read
// this one value — keep the surface and its reservation in one contract.
export const RESERVED_AUDIO_CARD_HEIGHT = 300

export function isAudioRecording(
    recording?: {
        original_recording_type?: unknown
        recording_type?: unknown
        recording_content_type?: unknown
    } | null,
): boolean {
    return [
        recording?.original_recording_type,
        recording?.recording_type,
        recording?.recording_content_type,
    ].some(contentType =>
        String(contentType || '')
            .toLowerCase()
            .startsWith('audio/'),
    )
}

// availableTileWidth: pass mobilePayloadMediaContentWidth() at RENDER/ESTIMATE time to clamp
// the frame to the width recordings actually render at. Geometry projections are retained
// across resizes, so they must call this WITHOUT a width and clamp later via recordingRatio.
export function resolveRecordingFrameHeight(
    recording?: {
        original_recording_type?: unknown
        recording_type?: unknown
        recording_content_type?: unknown
        video_width?: unknown
        video_height?: unknown
    } | null,
    availableTileWidth?: number | null,
): number {
    if (isAudioRecording(recording)) return RESERVED_AUDIO_RECORDING_HEIGHT

    const width = Number(recording?.video_width)
    const height = Number(recording?.video_height)
    return Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0
        ? bucketedMediaHeight(width, height, availableTileWidth)
        : RESERVED_DIMLESS_MEDIA_HEIGHT
}

// Constant chrome around an embedded answer (PostEmbed): border 1+1, padding 10+10, header row
// ~28. The nested answer inside is estimated recursively from its own payload.
export const POST_EMBED_CHROME_HEIGHT = 50

// Inspectable post media must remain fully visible. Resolution affects sharpness, not whether
// the application is allowed to crop content inside a geometry-locked frame.
export function resolveMediaFit(
    _width?: number | null,
    _height?: number | null,
    _boxHeight?: number | null,
    _boxWidth?: number | null,
): 'contain' {
    return 'contain'
}
